import { useEffect } from 'react'

import { getChannelClient } from 'src/utils/messages/singleton'
import { sendPublic } from 'src/utils/messages/publicProtocol'
import { buildCompletedDonationPayload } from 'src/utils/messages/completedDonation'

// Side effects that fire when a donation completes:
//   - Dispatch a `donationCompleted` custom event on this window so
//     any same-origin host JS can react to it.
//   - Send a `donationCompleted` public-protocol message to the
//     parent — sendPublic handles the v0 (legacy `DonateCompleted`
//     stringified envelope) + v1 (FSA broadcast) parallel emit per
//     the partner's data-message-version opt-in. Cross-origin
//     embedders only get the public-safe slice (no PII); same-origin
//     gets the full detail. Simulated donations skip the cross-
//     origin post (no point telling a real host site about a fake).
//   - Optionally rain hearts: same-window CustomEvent dispatch when
//     standalone (hearts-rain.js listens), channel-client send to the
//     embed-script host when embedded (Widgets.js#init's rainHearts
//     listener picks it up and re-dispatches as a CustomEvent on the
//     host page so hearts-rain.js fires there too).
//
// v6 still carries a `window.onbeforeunload = () => {}` guard added
// in late 2016 to suppress Safari's "you've entered information"
// dialog on the thank-you page after an AJAX submit. v7's Safari
// 16.4+ floor outpaces the buggy versions — verified manually on
// Safari 14.1 (v6 floor) and 26 that the dialog no longer misfires —
// so v7 drops the suppression. v6 keeps it; its Safari 14.0 floor
// is below where this was last spot-checked.

const useDonationCompletion = ({ donation, paymentMethod, brandColor, rainDownHearts }) => {
  useEffect(() => {
    if (!donation) return

    dispatchEvent(new CustomEvent('donationCompleted', { detail: donation }))

    const sameOrigin = (() => {
      try {
        return location.origin && location.origin == parent.location.origin
      } catch (e) {
        return false
      }
    })()

    if (sameOrigin || !donation.simulated) {
      // Same-origin embedders get the full donation; cross-origin gets the
      // public slice — amounts in the donation's own currency plus `currency`
      // + `currencyType` (see completedDonation.js).
      // `paymentMethod` is read from the closure of the render that set
      // `donation` — deliberately not an effect dep. Safe because it can't
      // change inside that window: the effect only fires on the render where
      // donation flips truthy, donateAgain clears donation without touching
      // paymentMethod (so the next donation re-fires this with a current
      // value), and CryptoInfo's deferred finished() is torn down if the donor
      // navigates back. Using the prop rather than `donation.paymentMethod` is
      // intentional too — only the Stripe path stamps that key onto the
      // donation; paypal / deferred / crypto / stock / DAF don't.
      const data = sameOrigin
        ? donation
        : buildCompletedDonationPayload(donation, paymentMethod)
      // Round-trip through JSON to strip non-structured-cloneable
      // values (partnerItem methods, etc.) — v0's stringify dropped
      // them silently but v1's structured-clone postMessage would
      // throw DataCloneError.
      sendPublic(parent, 'donationCompleted', JSON.parse(JSON.stringify(data)))
    }

    // Defer hearts a tick so donationCompleted lands first — gives any
    // listener a chance to react before the visual celebration.
    if (rainDownHearts) {
      setTimeout(() => {
        if (parent == self)
          dispatchEvent(new CustomEvent('rainHearts', { detail: brandColor }))
        else
          getChannelClient().send('rainHearts', brandColor)
      }, 1)
    }
  }, [ donation ])
}

export default useDonationCompletion
